import { useState } from "react";
import clsx from "clsx";
import {
  BotIcon,
  BriefcaseBusinessIcon,
  CopyIcon,
  DropletIcon,
  EllipsisIcon,
  LoaderIcon,
  SparklesIcon,
  UsersIcon,
} from "lucide-react";
import { useNavigate } from "react-router";

import { AgenticStaffingFlowStatusIndicator } from "./AgenticStaffingFlowStatusIndicator";
import { DuplicateJobConfirmationDialog } from "./DuplicateJobConfirmationDialog";
import { JobStatusDropdown } from "./JobStatusDropdown";

import {
  Column,
  EmptyDataDash,
  EmptyState,
  Row,
  Text,
} from "@/components";
import { DashboardLiquidityCell } from "@/components/dashboard/cells";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

import { useDuplicateJob } from "@/hooks/mutations/useDuplicateJob";
import { useRefetchJobs } from "@/hooks/refetches/useRefetchJobs";
import { useSetJobInJobs } from "@/hooks/sets/useSetJobInJobs";
import { dates } from "@/lib/dates";
import { mixpanelUtils } from "@/lib/mixpanel/mixpanel";
import { MIXPANEL_SCREENS } from "@/lib/mixpanel/types";
import { Job, JobsResponse } from "@/types";

export const JobsTable = ({ jobs }: { jobs: JobsResponse[] }) => {
  const {
    mutate: duplicate,
    isPending,
    isError: isDuplicateError,
    reset: resetDuplicate,
  } = useDuplicateJob();
  const navigate = useNavigate();
  const refetchJobs = useRefetchJobs();
  const setJobInJobs = useSetJobInJobs();
  const [
    isDuplicateConfirmationDialogOpen,
    setIsDuplicateConfirmationDialogOpen,
  ] = useState(false);
  const [jobToDuplicate, setJobToDuplicate] = useState<Job | undefined>(
    undefined,
  );

  const isEmptyStateForFilters = jobs.length === 0;

  const handleDuplicateJob = (linkToSource: boolean) => {
    if (!jobToDuplicate) {
      return;
    }

    duplicate(
      { jobId: jobToDuplicate.id, linkToSource },
      {
        onSuccess: () => {
          refetchJobs();
          setIsDuplicateConfirmationDialogOpen(false);
          setJobToDuplicate(undefined);
        },
      },
    );
  };

  const handleRowClick = (job: Job) => {
    mixpanelUtils.trackRowClick("Job", MIXPANEL_SCREENS.COMPANY);
    void navigate(`jobs/${job.id}`);
  };

  return (
    <Column className="overflow-auto">
      <div className="rounded-t-lg border border-b-0 border-slate-200">
        <Table>
          <TableHeader
            className={clsx(
              isEmptyStateForFilters && "pointer-events-none opacity-50",
            )}
          >
            <TableRow className="border-none">
              <TableHead className="w-[25%]">
                <Row className="items-center gap-1">
                  <BriefcaseBusinessIcon className="h-4 w-4 text-slate-700" />
                  <Text variant="header">Job title</Text>
                </Row>
              </TableHead>
              <TableHead className="w-[8%]">
                <Row className="items-center gap-1">
                  <DropletIcon className="h-4 w-4 text-slate-700" />
                  <Text variant="header">Liquidity</Text>
                </Row>
              </TableHead>
              <TableHead className="w-[14%]">
                <Row className="items-center gap-1">
                  <UsersIcon className="h-4 w-4 text-slate-700" />
                  <Text variant="header">Candidates</Text>
                </Row>
              </TableHead>
              <TableHead className="w-[14%]">
                <Row className="items-center gap-1">
                  <LoaderIcon className="h-4 w-4 text-slate-700" />
                  <Text variant="header">Status</Text>
                </Row>
              </TableHead>
              <TableHead className="w-[15%]">
                <Row className="items-center gap-1">
                  <BotIcon className="h-4 w-4 text-slate-700" />
                  <Text variant="header">Agentic Staffing Enabled</Text>
                </Row>
              </TableHead>
              <TableHead className="w-[15%]">
                <Row className="items-center gap-1">
                  <SparklesIcon className="h-4 w-4 text-slate-700" />
                  <Text variant="header">Agentic Scoring Rubric</Text>
                </Row>
              </TableHead>
              <TableHead className="w-[7%]" />
            </TableRow>
          </TableHeader>
        </Table>
      </div>
      <div className="overflow-y-auto rounded-b-lg border border-slate-200">
        {isEmptyStateForFilters ? (
          <EmptyState
            className="bg-slate-50 py-14"
            description="There aren't any jobs listed for this company."
            handlePrimaryClick={() => {
              void navigate("jobs/create");
            }}
            Icon={BriefcaseBusinessIcon}
            primaryButtonName="Create a job"
            primaryButtonVariant="outline"
            title="No jobs found"
          />
        ) : (
          <Table>
            <TableBody>
              {jobs.map((jobResponse) => {
                const { job, jobInternalConfig, totalCandidates } = jobResponse;
                return (
                  <TableRow
                    className="cursor-pointer"
                    key={job.id}
                    onClick={() => {
                      handleRowClick(job);
                    }}
                  >
                    <TableCell className="w-[25%]">
                      <Column className="gap-1">
                        <Text>{job.title}</Text>
                        <Text variant="muted">
                          {job.city}, {job.state} •{" "}
                          {dates.formatIsoDateForUser(job.createdAt)}
                        </Text>
                      </Column>
                    </TableCell>
                    <TableCell className="w-[8%]">
                      <DashboardLiquidityCell
                        breakdown={job.liquidityBreakdown}
                        liquidity={job.liquidity}
                        showGuidance
                      />
                    </TableCell>
                    <TableCell className="w-[14%]">
                      {/* Important to use `<span>` so entire width of cell doesn't get taken. */}
                      <span className="whitespace-nowrap rounded-lg border border-slate-200 p-2">
                        {totalCandidates} active candidate
                        {totalCandidates !== 1 && "s"}
                      </span>
                    </TableCell>
                    <TableCell className="w-[14%]">
                      <JobStatusDropdown job={job} setJob={setJobInJobs} />
                    </TableCell>
                    <TableCell className="w-[15%]">
                      <AgenticStaffingFlowStatusIndicator
                        active={jobInternalConfig?.isAgentFlowEnabled ?? false}
                      />
                    </TableCell>
                    <TableCell className="w-[15%]">
                      {jobInternalConfig?.autoProfileRatingRubric ? (
                        <SparklesIcon className="h-6 w-6 text-green-500" />
                      ) : (
                        <EmptyDataDash />
                      )}
                    </TableCell>
                    <TableCell className="w-[7%]">
                      <DropdownMenu>
                        <DropdownMenuTrigger>
                          <Button size="icon" variant="ghost">
                            <EllipsisIcon className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem
                            onClick={(e) => {
                              // So the table row doesn't get clicked.
                              e.stopPropagation();
                              // Clear any previous attempt's error state.
                              resetDuplicate();
                              setJobToDuplicate(job);
                              setIsDuplicateConfirmationDialogOpen(true);
                            }}
                          >
                            <CopyIcon className="h-4 w-4" /> Duplicate
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </div>
      <DuplicateJobConfirmationDialog
        isError={isDuplicateError}
        isLoading={isPending}
        onConfirm={handleDuplicateJob}
        onOpenChange={setIsDuplicateConfirmationDialogOpen}
        open={isDuplicateConfirmationDialogOpen}
      />
    </Column>
  );
};
